import { useCallback, useState } from 'react'
import config from '@/config'
import { getToken } from '@/utils/AuthTokenHelper'

/**
 * Files on their way into the conversation.
 *
 * IT USES THE ASSET PATH THAT ALREADY EXISTS, which is the whole point of
 * this file being rewritten. The first version invented an upload route, a
 * bucket helper and a reading tool; all three existed already and were
 * better. The real flow is three steps and lives in
 * `trunk/src/mastra/routes/assets.ts`:
 *
 *   1. presign  — mime allowlist, per-type size cap, creates an `Asset`
 *                 row with status PENDING and hands back a signed PUT
 *   2. PUT      — the bytes go straight to S3, not through trunk
 *   3. confirm  — flips the row to UPLOADED
 *
 * Nothing is public: the object has no public-read ACL, and the agent
 * never receives a URL. It gets the ASSET ID and reads the bytes
 * server-side with the task IAM role, through `view-attachment` for an
 * image or `fetch-to-scratchpad` for data. A model that can be handed a
 * url is a model that can be talked into being handed somebody else's.
 *
 * UPLOADED ON SELECTION, not on send: by the time somebody has typed the
 * sentence that goes with a screenshot, the screenshot is already there,
 * and a failure surfaces while they are still looking at what failed.
 */

export interface PendingFile {
  name: string
  kind: 'image' | 'file'
  mediaType: string
  /** Set once the upload has landed and been confirmed. */
  assetId?: string
  /** A local object URL, purely so the panel can show a thumbnail. */
  url?: string
  error?: string
}

export function kindOf(mediaType: string): 'image' | 'file' {
  return mediaType.startsWith('image/') ? 'image' : 'file'
}

/**
 * Six at once.
 *
 * `view-attachment` reads at most six images in a call, so more than six
 * is a set the agent cannot look at in one go. Overflow is REPORTED
 * rather than silently dropped: a photo that vanished without a word is
 * worse than one that was refused.
 */
export const MAX_FILES = 6

/** What the turn carries. An id, never a url. */
export interface TurnAsset {
  assetId: string
  name: string
  mediaType: string
  /** Local object URL, so the transcript can show it back. Never sent on. */
  previewUrl?: string
}

export function useAttachments(
  orgId: string | undefined,
  threadId: string | undefined,
): {
  pending: PendingFile[]
  add: (files: File[]) => void
  remove: (name: string) => void
  clear: () => void
  /** Only the ones that actually landed, for sending with a turn. */
  ready: () => TurnAsset[]
} {
  const [pending, setPending] = useState<PendingFile[]>([])

  const add = useCallback(
    (files: File[]) => {
      if (!orgId || !threadId) return
      const token = getToken()?.accessToken ?? ''
      const base = config.url.trunk

      setPending((now) => {
        const room = MAX_FILES - now.length
        const taking = files.slice(0, Math.max(0, room))
        const refused = files.length - taking.length

        for (const file of taking) {
          const entry: PendingFile = {
            name: file.name,
            kind: kindOf(file.type),
            mediaType: file.type || 'application/octet-stream',
            // Local, revoked by the browser on unload. Never sent anywhere.
            url: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
          }

          const fail = (why: string) => {
            setPending((state) =>
              state.map((p) => (p.name === file.name ? { ...p, error: why } : p)),
            )
          }

          void (async () => {
            try {
              const presigned = await fetch(`${base}/organisations/${orgId}/assets/presign`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  threadId,
                  filename: file.name,
                  mimeType: entry.mediaType,
                  size: file.size,
                }),
              })
              if (!presigned.ok) {
                // The route says WHY (type not allowed, too large), and
                // that sentence is more use than "failed".
                const said = (await presigned.json().catch(() => null)) as
                  | { error?: string }
                  | null
                fail(said?.error ?? `refused (${String(presigned.status)})`)
                return
              }
              const { assetId, uploadUrl } = (await presigned.json()) as {
                assetId: string
                uploadUrl: string
              }

              const put = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': entry.mediaType },
                body: file,
              })
              if (!put.ok) {
                fail('would not upload')
                return
              }

              await fetch(`${base}/organisations/${orgId}/assets/${assetId}/confirm`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
              })

              setPending((state) =>
                state.map((p) => (p.name === file.name ? { ...p, assetId } : p)),
              )
            } catch {
              fail('would not upload')
            }
          })()
        }

        const next = [...now, ...taking.map((file) => ({
          name: file.name,
          kind: kindOf(file.type),
          mediaType: file.type || 'application/octet-stream',
          url: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        }))]

        return refused > 0
          ? [
              ...next,
              {
                name: `${String(refused)} more not added`,
                kind: 'file' as const,
                mediaType: 'text/plain',
                error: `${String(MAX_FILES)} at a time`,
              },
            ]
          : next
      })
    },
    [orgId, threadId],
  )

  const remove = useCallback((name: string) => {
    setPending((now) => {
      const going = now.find((p) => p.name === name)
      if (going?.url) URL.revokeObjectURL(going.url)
      return now.filter((p) => p.name !== name)
    })
  }, [])

  /**
   * Emptied on send, and the object URLs are NOT revoked.
   *
   * The transcript is now showing them: revoking here turned every image
   * in the conversation into a broken thumbnail the moment it was sent.
   * The browser releases them on unload, and a handful of blob handles
   * for the length of a conversation is nothing.
   */
  const clear = useCallback(() => {
    setPending([])
  }, [])

  const ready = useCallback(
    (): TurnAsset[] =>
      pending
        .filter((file): file is PendingFile & { assetId: string } => Boolean(file.assetId))
        .map((file) => ({
          assetId: file.assetId,
          name: file.name,
          mediaType: file.mediaType,
          ...(file.url ? { previewUrl: file.url } : {}),
        })),
    [pending],
  )

  return { pending, add, remove, clear, ready }
}
